import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { dateLocaleFor, useHubLang } from '../lib/i18n'
import { PROFILE } from '../lib/hubCopy'
import { signedPhotoUrl } from '../lib/photos'
import { myTaggedPhotos, type TaggedPhoto } from '../lib/photoTags'
import { Dialog } from './Dialog'

/**
 * Photos you are tagged in, on Profile — 143 (FED-49), reshaped by 170.
 *
 * ── NO BOX (David, 11 Oct) ──────────────────────────────────────────────────
 *
 * Until today each photo here carried a "Company marketing" tick box. David:
 * "it should not be a toggle check box. Photos you're tagged in opens a modal
 * where there is a list of photos and they can go to the post and then on the
 * post they can remove tag or request review." So the card is a count and a
 * button; the dialog lists the photos with who tagged you and where you stand;
 * every act happens on the post, in the viewer, where the photo is — Remove
 * my tag, and Request review with "I'm in this photo" for not wanting the
 * company to use it, open for 30 days (170's window).
 *
 * RENDERS NOTHING until somebody has tagged you: an empty card saying nobody
 * has is clutter on everybody's profile.
 */
export function PhotosOfMe() {
  const lang = useHubLang()
  const t = PROFILE[lang]
  const [rows, setRows] = useState<TaggedPhoto[] | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    let live = true
    myTaggedPhotos()
      .then((r) => live && setRows(r))
      .catch(() => live && setRows([]))
    return () => {
      live = false
    }
  }, [])

  if (rows === null || rows.length === 0) return null

  const day = (iso: string) =>
    new Date(iso).toLocaleDateString(dateLocaleFor(lang), { day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <section className="card profile__card" aria-labelledby="photos-of-me-h">
      <h2 id="photos-of-me-h" className="card__title">
        {t.photosHead}
      </h2>
      <p className="profile__lede">{t.photosLede}</p>
      <div className="photosofme__summary">
        <p className="photosofme__count">{t.photosCount(rows.length)}</p>
        <button type="button" className="btn btn--quiet" onClick={() => setOpen(true)} aria-haspopup="dialog">
          {t.photosSee}
        </button>
      </div>

      <Dialog open={open} title={t.photosDialogTitle} onClose={() => setOpen(false)} className="photosofme__dialog">
        <p className="photosofme__howto">{t.photosHowTo}</p>
        <ul className="photosofme">
          {rows.map((r) => (
            <li key={r.path} className="photosofme__row">
              <Thumb path={r.path} />
              <div className="photosofme__main">
                <p className="photosofme__by">
                  {t.photosTaggedBy(r.taggedByName)} · {day(r.taggedAt)}
                  {r.removedAt && <> · {t.photosTagRemoved}</>}
                </p>
                <p className={`photosofme__status${r.consentOpen || r.reviewOpen ? '' : ' photosofme__status--done'}`}>
                  {statusOf(r, t, day)}
                </p>
                <Link
                  className="photosofme__open"
                  to={`/item/${r.contentId}?photo=${encodeURIComponent(r.path)}`}
                  onClick={() => setOpen(false)}
                >
                  {t.photosOpen}
                </Link>
              </div>
            </li>
          ))}
        </ul>
      </Dialog>
    </section>
  )
}

/** Where you stand on one photo, in one line. Exported for the suite. */
export function statusOf(
  r: Pick<TaggedPhoto, 'isMine' | 'reviewOpen' | 'reviewNoConsent' | 'consentOpen' | 'consentUntil'>,
  t: (typeof PROFILE)['en'],
  day: (iso: string) => string,
): string {
  if (r.isMine) return t.photosOwn
  if (r.reviewNoConsent) return t.photosAskedNoConsent
  if (r.reviewOpen) return t.photosAsked
  if (r.consentOpen && r.consentUntil) return t.photosConsentUntil(day(r.consentUntil))
  return t.photosConsentPassed
}

function Thumb({ path }: { path: string }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    signedPhotoUrl(path)
      .then((u) => live && setUrl(u))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [path])
  return url ? (
    <img className="photosofme__thumb" src={url} alt="" />
  ) : (
    <span className="photosofme__thumb" aria-hidden="true" />
  )
}
